import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Divider,
  Grid,
} from '@mui/material';
import axiosApi from '../Utils/axiosApi';
import checkPermission from '../Utils/checkPermission';
const BillingConfigDialog = ({ open, onClose, hostelList, onSaved }) => {
  const alertRef = useRef(null);

  const [message, setMessage] = useState(null);

  const [billingConfig, setBillingConfig] = useState({
    hostelId: '',
    hostelName: '',
    billingMode: 'PRE_BILLING',
    foodCostCalculationMode: 'FOOD_PER_MONTH', // FOOD_DIVIDING_SYSTEM | FOOD_PER_MONTH
    lateFee: '',
    gracePeriod: '',
    validityFromDate: '',
    validityToDate: '',
    rentDeductionAbsence: '',
    rentDeductionClosure: '',
    minConsecutiveAbsenceDaysRent: '',
    minConsecutiveAbsenceDaysFood: '',
    billDueDateOffsetDaysOfMonth: '',
	 foodReductionDaysPerSlab: '', 
    reminderWhatsAppNumber: '',
  });

  /* ---------------- API ---------------- */

  const saveBillingConfigService = (payload) =>
    axiosApi.post(
      '/hostelerBillingConfiguration/updateHostelerBillingConfiguration',
      payload
    );

  const getBillingConfigService = (hostelId) =>
    axiosApi.post(
      '/hostelerBillingConfiguration/getHostelerBillingConfiguration',
      { hostelIds: [hostelId] }
    );

  /* ---------------- Effects ---------------- */

  useEffect(() => {
    if (message) {
      setTimeout(() => {
        alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        alertRef.current?.focus();
      }, 100);
    }
  }, [message]);

  /* ---------------- Validation ---------------- */

  const validateFields = () => {
    if (!billingConfig.hostelId) {
      setMessage({ type: 'error', text: 'Please select a hostel.' });
      return false;
    }

    if (!billingConfig.validityFromDate || !billingConfig.validityToDate) {
      setMessage({ type: 'error', text: 'Please select validity dates.' });
      return false;
    }

    if (
      billingConfig.billDueDateOffsetDaysOfMonth &&
      (billingConfig.billDueDateOffsetDaysOfMonth < 1 ||
        billingConfig.billDueDateOffsetDaysOfMonth > 31)
    ) {
      setMessage({
        type: 'error',
        text: 'Bill due date must be between 1 and 31.',
      });
      return false;
    }

    return true;
  };

  /* ---------------- Save ---------------- */

  const handleSave = async () => {
    if (!validateFields()) return;

    try {
      const dto = {
        hostelId: billingConfig.hostelId,
        hostelName: billingConfig.hostelName,
        billingMode: billingConfig.billingMode,
        foodCostCalculationMode: billingConfig.foodCostCalculationMode,
        penaltyLateFees: Number(billingConfig.lateFee) || 0,
        gracePeriodDaysForPenalty: Number(billingConfig.gracePeriod) || 0,
        validityFromDate: billingConfig.validityFromDate,
        validityToDate: billingConfig.validityToDate,
        dailyRentAbsenceDeductionPercent:
          Number(billingConfig.rentDeductionAbsence) || null,
        dailyRentHostelClosureDeductionPercent:
          Number(billingConfig.rentDeductionClosure) || null,
        minConsecutiveAbsenceDaysRent:
          Number(billingConfig.minConsecutiveAbsenceDaysRent) || null,
        minConsecutiveAbsenceDaysFood:  
		Number(billingConfig.minConsecutiveAbsenceDaysFood) || null,
        foodReductionDaysPerSlab:
			billingConfig.foodCostCalculationMode === 'FOOD_DIVIDING_SYSTEM'
			? Number(billingConfig.foodReductionDaysPerSlab) || null
			: null,
        billDueDateOffsetDaysOfMonth:
          Number(billingConfig.billDueDateOffsetDaysOfMonth) || null,
		  
      };

      await saveBillingConfigService({
        hostelerBillingConfigurationDTO: dto,
      });

      setMessage({
        type: 'success',
        text: 'Billing configuration saved successfully!',
      });
      onSaved && onSaved();
    } catch (err) {
      console.error(err);
      setMessage({
        type: 'error',
        text: 'Failed to save billing configuration.',
      });
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => reason !== 'backdropClick' && onClose()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        Billing Configuration
      </DialogTitle>

      <DialogContent sx={{ maxHeight: '75vh', overflowY: 'auto' }}>
        {message && (
          <Box sx={{ mb: 2 }}>
            <Alert
              ref={alertRef}
              tabIndex={-1}
              severity={message.type}
              onClose={() => setMessage(null)}
            >
              {message.text}
            </Alert>
          </Box>
        )}

        {/* Hostel */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Hostel</InputLabel>
          <Select
            value={billingConfig.hostelId}
            label="Hostel"
            onChange={async (e) => {
              const hostelId = e.target.value;
              const hostel = hostelList.find((h) => h.id === hostelId);

              setBillingConfig((prev) => ({
                ...prev,
                hostelId,
                hostelName: hostel?.hostelName || '',
              }));

              try {
                const { data } = await getBillingConfigService(hostelId);
                if (data?.length) {
                  const dto = data[0];
                  setBillingConfig((prev) => ({
                    ...prev,
                    hostelId: dto.hostelId,
                    hostelName: dto.hostelName,
                    billingMode: dto.billingMode || 'PRE_BILLING',
                    foodCostCalculationMode:
                      dto.foodCostCalculationMode || 'FOOD_PER_MONTH',
                    lateFee: dto.penaltyLateFees?.toString() || '',
                    gracePeriod:
                      dto.gracePeriodDaysForPenalty?.toString() || '',
                    validityFromDate:
                      dto.validityFromDate?.split('T')[0] || '',
                    validityToDate:
                      dto.validityToDate?.split('T')[0] || '',
                    rentDeductionAbsence:
                      dto.dailyRentAbsenceDeductionPercent?.toString() || '',
                    rentDeductionClosure:
                      dto.dailyRentHostelClosureDeductionPercent?.toString() ||
                      '',
                    minConsecutiveAbsenceDaysRent:
                      dto.minConsecutiveAbsenceDaysRent?.toString() || '',
                    minConsecutiveAbsenceDaysFood:
                      dto.minConsecutiveAbsenceDaysFood?.toString() || '',
                    billDueDateOffsetDaysOfMonth:
                      dto.billDueDateOffsetDaysOfMonth?.toString() || '',
                    reminderWhatsAppNumber:
                      dto.reminderWhatsAppNumber || '',
					  
					  foodReductionDaysPerSlab:
						dto.foodReductionDaysPerSlab?.toString() || '',
                  }));
                }
              } catch {
                setMessage({
                  type: 'error',
                  text: 'Failed to load billing configuration.',
                });
              }
            }}
          >
            {hostelList.map((h) => (
              <MenuItem key={h.id} value={h.id}>
                {h.hostelName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Billing Mode */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Billing Mode</InputLabel>
          <Select
            value={billingConfig.billingMode}
            label="Billing Mode"
            onChange={(e) =>
              setBillingConfig({
                ...billingConfig,
                billingMode: e.target.value,
              })
            }
          >
            <MenuItem value="PRE_BILLING">Pre Billing</MenuItem>
            <MenuItem value="POST_BILLING">Post Billing</MenuItem>
          </Select>
        </FormControl>

        {/* Food Mode */}
        <FormControl fullWidth sx={{ mb: 2 }}>
  <InputLabel>Food Cost Calculation Mode</InputLabel>
  <Select
    value={billingConfig.foodCostCalculationMode}
    label="Food Cost Calculation Mode"
    onChange={(e) =>
      setBillingConfig({
        ...billingConfig,
        foodCostCalculationMode: e.target.value,
      })
    }
  >
    <MenuItem value="FOOD_DIVIDING_SYSTEM">
      Food Dividing System (Mess Based)
    </MenuItem>
    <MenuItem value="FOOD_PER_MONTH">
      Fixed Food Per Month
    </MenuItem>
  </Select>
</FormControl>


        <Divider sx={{ my: 2 }}>Validity</Divider>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              type="date"
              label="From"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={billingConfig.validityFromDate}
              onChange={(e) =>
                setBillingConfig({
                  ...billingConfig,
                  validityFromDate: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              type="date"
              label="To"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={billingConfig.validityToDate}
              onChange={(e) =>
                setBillingConfig({
                  ...billingConfig,
                  validityToDate: e.target.value,
                })
              }
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }}>Deductions</Divider>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Min Absence Days (Rent)"
              type="number"
              fullWidth
              value={billingConfig.minConsecutiveAbsenceDaysRent}
              onChange={(e) =>
                setBillingConfig({
                  ...billingConfig,
                  minConsecutiveAbsenceDaysRent: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
  label="Min Absence Days (Food)"
  fullWidth
  type="number"
  value={billingConfig.minConsecutiveAbsenceDaysFood}
  onChange={(e) =>
    setBillingConfig({
      ...billingConfig,
      minConsecutiveAbsenceDaysFood: e.target.value,
    })
  }
/>

          </Grid>
		  
		
{billingConfig.foodCostCalculationMode === 'FOOD_DIVIDING_SYSTEM' && (
  <Grid item xs={6}>
    <TextField
      label="Food Reduction Days per Slab"
      type="number"
      fullWidth
      helperText="Days reduced for each absence slab (e.g. 3)"
      value={billingConfig.foodReductionDaysPerSlab}
      onChange={(e) =>
        setBillingConfig({
          ...billingConfig,
          foodReductionDaysPerSlab: e.target.value,
        })
      }
    />
  </Grid>
)}



        </Grid>

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={!checkPermission("Hostel", "Hosteler Bill", "UPDATEBILLINGCONFIGURATION")}>
            Save
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BillingConfigDialog;
